import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid2,
	Paper,
	Typography
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import {
	usePatchMyPresentationFreelancerOnboardingStep,
	useSaveMyPresentationFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { zoullooColors } from "../../../theme";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import FreelancerOnboardingMyPresentationExperiencesBloc from "./components/experiences/FreelancerOnboardingMyPresentationExperiencesBloc";
import FreelancerOnboardingMyPresentationPresentationBloc from "./components/experiences/FreelancerOnboardingMyPresentationPresentationBloc";
import FreelancerOnboardingMyPresentationTrainerBloc from "./components/experiences/FreelancerOnboardingMyPresentationTrainerBloc";
import i18n from "../../../i18n/i18n";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";
import { useTranslatableForm } from "../../../hooks/useTranslatableForm";
import {
	PROFESSIONAL_EXPERIENCE_ENTITY,
	usePresentationStepContentInAnyLanguage
} from "../../../hooks/usePresentationStepContentInAnyLanguage";
import { scheduleTranslationRefresh } from "../../../utils/translationRefresh";
import { LanguageCode } from "../../../models/enums/LanguageCode";
import { saveFreelancerOnboardingProgression, saveProfessionalExperienceAsync } from "../../../api/freelancer-profiles-api";
import JSONPatchOperationType from "../../../utils/JSONPatchOperationType";

// Helper function to normalize URLs for validation
export const normalizeUrl = (url: string): string => {
	if (!url) return "";

	let normalizedUrl = url.trim();

	if (normalizedUrl.startsWith("www.") || !/^https?:\/\//i.test(normalizedUrl)) {
		normalizedUrl = `https://${normalizedUrl}`;
	}

	return normalizedUrl;
};

export default function FreelancerOnboardingMyPresentation() {
	// State for edit/display modes
	const [experienceEditMode, setExperienceEditMode] = useState<{
		[key: number]: boolean;
	}>({});
	const {
		setFreelancerOnboardingMyPresentationStepViewModel,
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		freelancerProfileQueryResult,
		setSaveTranslationsAsync
	} = useFreelancerContext();
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;
	const patchMyPresentationFreelancerOnboarding =
		usePatchMyPresentationFreelancerOnboardingStep();
	const saveMyPresentationFreelancerOnboardingProgress =
		useSaveMyPresentationFreelancerOnboardingProgress({ hideAllSnackbars: true });
	const { t } = useTranslation();
	const { interceptNavigation } = useNavigationInterceptorContext();
	const queryClient = useQueryClient();
	const isFilledInAnyLanguage = usePresentationStepContentInAnyLanguage(freelancerProfile);
	// Read through a ref: the delayed view-model push below validates with its mount-time closure,
	// which would otherwise hold the checker from before the en cells loaded.
	const isFilledInAnyLanguageRef = useRef(isFilledInAnyLanguage);
	isFilledInAnyLanguageRef.current = isFilledInAnyLanguage;
	const [
		isCityValueChangedOnLoadBecauseOfBeingTranslated,
		setIsCityValueChangedOnLoadBecauseOfBeingTranslated
	] = useState(true);

	// Translatable form state for the presentation field
	const translatableForm = useTranslatableForm({
		entityType: "FreelancerProfile",
		entityId: freelancerProfile?.id,
		sourceLanguage: freelancerProfile?.freelancer?.user?.settings?.preferredLanguage,
		translatableFields: ["presentation"],
		initialValues: {
			presentation: freelancerProfile?.presentation ?? "",
		},
	});

	// Ref to avoid stale closure in formik onSubmit
	const translatableFormRef = useRef(translatableForm);
	translatableFormRef.current = translatableForm;

	// Ref to hold experience translatable forms (shared with ExperienceListContainer). Keys are
	// PREFIXED ("id:<dbId>" / "idx:<arrayIndex>"): DB ids and array indexes share small integers, so
	// a raw `id ?? index` key lets a NEW experience collide with an existing one whose id equals its
	// index — translations would then be read from / written onto the wrong experience.
	const experienceTranslatableFormsRef = useRef<Record<string, ReturnType<typeof useTranslatableForm> | null>>({});

	// A translatable experience field counts as filled if it has content in ANY language. The FR may
	// legitimately be empty when the talent's source language is EN (the worker generates the FR after).
	// Look up the experience's translatable form by id, falling back to its array index (new experiences
	// have no id yet).
	const experienceFieldFilledInAnyLanguage = (
		path: string | undefined,
		parent: { id?: number } | undefined,
		field: string
	): boolean => {
		// Primary-language (FR) content lives directly on the experience in formik — covers FR-source
		// talents and any experience whose FR column the worker already projected (including after a
		// reload). Avoids depending on a mounted form for those cases.
		const primaryValue = (parent as Record<string, unknown> | undefined)?.[field];
		if (typeof primaryValue === "string" && primaryValue.trim() !== "" && primaryValue !== "<p></p>") {
			return true;
		}
		// Otherwise rely on the experience's translatable form: the live one while editing, or the
		// frozen snapshot ExperienceForm leaves behind once collapsed (so an EN-only experience with
		// an empty FR column doesn't block the step after being confirmed). An experience never
		// opened in this session has no form: its saved cells answer instead.
		const byId = parent?.id != null ? experienceTranslatableFormsRef.current[`id:${parent.id}`] : null;
		const indexMatch = /professionalExperiences\[(\d+)\]/.exec(path ?? "");
		const byIndex = indexMatch
			? experienceTranslatableFormsRef.current[`idx:${indexMatch[1]}`]
			: null;
		const experienceForm = byId ?? byIndex;
		if (experienceForm) {
			return experienceForm.hasValueInAnyLanguage(field);
		}
		return isFilledInAnyLanguageRef.current(PROFESSIONAL_EXPERIENCE_ENTITY, parent?.id, field, undefined);
	};

	const formValidation = Yup.object({
		presentation: Yup.string()
			.test(
				"presentation-required",
				t("freelancerOnboardingMyPresentation.validation.presentationRequired"),
				function (value) {
					// Valid if FR has content
					if (value && value !== "<p></p>") return true;
					// Also valid if any other language has content. VALUE-based on purpose: the
					// modified-tracking is purged after each successful save (markLanguageSaved),
					// so a saved-then-revisited EN presentation would wrongly fail a tracking-based
					// check even though the server state is valid.
					return translatableFormRef.current.hasValueInAnyLanguage("presentation");
				}
			)
			.max(7000, obj =>
				t("freelancerOnboardingMyPresentation.validation.presentationMaxLength", {
					max: obj.max
				})
			),
		professionalExperiences: Yup.array()
			.of(
				Yup.object().shape({
					companyName: Yup.string().required(
						t("freelancerOnboardingMyPresentation.validation.companyNameRequired")
					),
					sector: Yup.object().required(
						t("freelancerOnboardingMyPresentation.validation.sectorRequired")
					),
					jobTitle: Yup.string().test(
						"jobTitle-required",
						t("freelancerOnboardingMyPresentation.validation.jobTitleRequired"),
						(value, context) => {
							if (value && value.trim()) return true;
							return experienceFieldFilledInAnyLanguage(context.path, context.parent, "jobTitle");
						}
					),
					startDate: Yup.string().required(
						t("freelancerOnboardingMyPresentation.validation.startDateRequired")
					),
					endDate: Yup.string().when("isCurrentPosition", {
						is: false,
						then: schema =>
							schema
								.required(
									t("freelancerOnboardingMyPresentation.validation.endDateRequired")
								)
								.test(
									"is-after-start-date",
									t(
										"freelancerOnboardingMyPresentation.validation.endDateMustBeAfterStartDate"
									),
									function (value, context) {
										const { startDate } = context.parent;
										if (!value || !startDate) return true;
										return new Date(value) > new Date(startDate);
									}
								),
						otherwise: schema => schema.nullable()
					}),
					city: Yup.object().required(
						t("freelancerOnboardingMyPresentation.validation.cityRequired")
					),
					missionDescription: Yup.string()
						.test(
							"missionDescription-required",
							t(
								"freelancerOnboardingMyPresentation.validation.missionDescriptionRequired"
							),
							(value, context) => {
								if (value && value.trim() && value !== "<p></p>") return true;
								return experienceFieldFilledInAnyLanguage(
									context.path,
									context.parent,
									"missionDescription"
								);
							}
						)
						.max(4000, obj =>
							t(
								"freelancerOnboardingMyPresentation.validation.missionDescriptionMaxLength",
								{
									max: obj.max
								}
							)
						),
					results: Yup.string().max(4000, obj =>
						t("freelancerOnboardingMyPresentation.validation.resultsMaxLength", {
							max: obj.max
						})
					),
					technicalStack: Yup.string().max(4000, obj =>
						t("freelancerOnboardingMyPresentation.validation.technicalStackMaxLength", {
							max: obj.max
						})
					),
					documents: Yup.array()
						.max(5, t("freelancerOnboardingMyPresentation.validation.documentsMaxCount"))
						.test(
							"unique-documents",
							t("freelancerOnboardingMyPresentation.validation.documentDuplicate"),
							function (documents) {
								if (!documents || documents.length === 0) return true;
								const names = documents
									.map((doc: any) => doc.name?.toLowerCase())
									.filter(Boolean);
								const uniqueNames = new Set(names);
								return names.length === uniqueNames.size;
							}
						),
					urls: Yup.array()
						.max(5, t("freelancerOnboardingMyPresentation.validation.urlsMaxCount"))
						.of(
							Yup.object().shape({
								url: Yup.string().test(
									"is-valid-url",
									t("freelancerOnboardingMyPresentation.validation.invalidUrlFormat"),
									function (value) {
										if (!value) return true; // Allow empty values

										const normalizedUrl = normalizeUrl(value);

										try {
											const urlObj = new URL(normalizedUrl);

											// Check if the hostname is valid
											const hostname = urlObj.hostname;

											// Basic validation: must contain at least one dot and a valid TLD
											const domainRegex =
												/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

											// Allow localhost for development
											if (hostname !== "localhost" && !domainRegex.test(hostname)) {
												return false;
											}

											// Reject obvious test/placeholder domains
											const invalidDomains = [
												"test",
												"example",
												"placeholder",
												"demo",
												"temp"
											];
											if (invalidDomains.includes(hostname.toLowerCase())) {
												return false;
											}

											return true;
										} catch {
											return false;
										}
									}
								)
							})
						)
						.test(
							"unique-urls",
							t("freelancerOnboardingMyPresentation.validation.urlDuplicate"),
							function (urls) {
								if (!urls || urls.length === 0) return true;

								// Normalize URLs for comparison
								const normalizedUrls = urls
									.map((urlItem: any) => {
										if (!urlItem?.url) return "";

										return normalizeUrl(urlItem.url).replace("www.", "").toLowerCase();
									})
									.filter(Boolean);

								const uniqueUrls = new Set(normalizedUrls);

								return normalizedUrls.length === uniqueUrls.size;
							}
						)
				})
			)
			.min(1, t("freelancerOnboardingMyPresentation.validation.experiencesRequired"))
	});

	const initialValues: FreelancerOnboardingMyPresentationStepViewModel = {
		presentation: freelancerProfile?.presentation ?? "",
		professionalExperiences:
			freelancerProfile?.professionalExperiences?.map(exp => {
				return {
					id: exp.id,
					freelancerProfileId: freelancerProfile.id,
					isTopExperience: exp.isTopExperience,
					city:
						exp.cityId && exp.cityName
							? {
									id: exp.cityId,
									label: exp.cityName
							  }
							: null,
					companyName: exp.companyName,
					sector: exp.sector,
					jobTitle: exp.jobTitle,
					jobType: exp.jobType,
					startDate: exp.startDate,
					endDate: exp.endDate,
					isCurrentPosition: exp.isCurrentPosition,
					jobLocation: exp.jobLocation,
					missionDescription: exp.missionDescription,
					results: exp.results ?? "",
					technicalStack: exp.technicalStack ?? "",
					skills: exp.skills,
					businessDomains: exp.businessDomains,
					documents: exp.documents,
					urls: exp.urls
				} as FreelancerOnboardingMyPresentationExperienceViewModel;
			}) ?? [],
		isTrainer: freelancerProfile?.isTrainer ?? false,
		form: null
	};

	const formik = useFormik<FreelancerOnboardingMyPresentationStepViewModel>({
		initialValues,
		onSubmit: async values => {
			try {
				// Save the FR value as-is; never copy another language into it.
				const submitValues = { ...values };

				await patchMyPresentationFreelancerOnboarding.mutateAsync({
					freelancerOnboardingMyPresentationStepViewModel: submitValues,
					freelancerProfile: freelancerProfile!,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MyPresentation
							: FreelancerOnboardingFormStep.MyAvailability
				});

				// Save edited translations for every language, including the primary one (FR).
				// Both languages are treated identically; the backend isAiTranslated flag protects
				// human-authored cells. A field is only sent if the user actually typed it.
				const modifiedLanguages = translatableFormRef.current.getModifiedLanguages();
				for (const { languageCode, fields } of modifiedLanguages) {
					// Send the edited value even when empty (clearing must persist).
					if (!("presentation" in fields)) continue;

					await saveFreelancerOnboardingProgression(
						freelancerProfile!,
						[{ op: JSONPatchOperationType.Replace, path: "/presentation", value: fields.presentation }],
						languageCode.toLowerCase()
					);
					// Saved: clear the tracking, or the next save would re-send this text as a HUMAN
					// edit (re-stamping a cell the user may hand back to the AI in between).
					translatableFormRef.current.markLanguageSaved(languageCode);
				}

				// Invalidate translation cache so next load picks up saved translations (scoped: only
				// the profile's own cells changed here). Flags too: the save may have created a human
				// cell or un-staled one — the badge and AI toggle read the flags query.
				// Staggered: the worker translates asynchronously — a single immediate refetch
				// lands before the translated cells exist (user had to reload to see them).
				scheduleTranslationRefresh(queryClient, "FreelancerProfile", freelancerProfile!.id);

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(FreelancerOnboardingFormStep.MyAvailability);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		},
		validationSchema: formValidation,
		enableReinitialize: true
	});

	// Expose translation save callback to the floating save button via context
	// Saves both Presentation AND Experience translations
	useEffect(() => {
		const saveTranslations = async () => {
			// 1. Save presentation translations
			const modified = translatableFormRef.current.getModifiedLanguages();
			for (const { languageCode, fields } of modified) {
				// Send the edited value even when empty (clearing must persist).
				if (!("presentation" in fields)) continue;
				await saveFreelancerOnboardingProgression(
					freelancerProfile!,
					[{ op: JSONPatchOperationType.Replace, path: "/presentation", value: fields.presentation }],
					languageCode.toLowerCase()
				);
				translatableFormRef.current.markLanguageSaved(languageCode);
			}

			// 2. Save experience translations (prefixed keys: "id:<dbId>" / "idx:<arrayIndex>")
			const expForms = experienceTranslatableFormsRef.current;
			for (const [expKey, tf] of Object.entries(expForms)) {
				if (!tf) continue;
				const expModified = tf.getModifiedLanguages();
				for (const { languageCode, fields } of expModified) {
					if (languageCode === LanguageCode.Fr) continue;

					// id-keyed entries resolve by DB id; idx-keyed ones (new experiences) resolve by
					// array position and are skipped below until they actually have an id. Never match
					// an idx key against DB ids — that wrote one experience's text onto another.
					const exp = expKey.startsWith("id:")
						? formik.values.professionalExperiences.find(e => e.id === Number(expKey.slice(3)))
						: formik.values.professionalExperiences[Number(expKey.slice(4))];
					if (!exp?.id) continue;

					// Untouched fields carry the CURRENT value of this language, never "": an empty
					// value reads as a per-language CLEAR server-side, which 400s on the required
					// fields when no other human cell backs them (e.g. AI-generated FR cells for an
					// EN-source talent).
					const langValues = tf.getValuesForLanguage(languageCode);
					const valueOf = (field: string) =>
						fields[field] !== undefined ? fields[field] : langValues[field] ?? "";

					await saveProfessionalExperienceAsync(
						freelancerProfile!.id,
						{
							id: exp.id,
							freelancerProfileId: exp.freelancerProfileId,
							isTopExperience: exp.isTopExperience,
							cityId: exp.city?.id ?? "",
							cityName: exp.city?.label ?? "",
							companyName: exp.companyName,
							sector: exp.sector,
							jobTitle: valueOf("jobTitle"),
							jobType: exp.jobType,
							startDate: exp.startDate,
							endDate: exp.endDate,
							isCurrentPosition: exp.isCurrentPosition,
							jobLocation: exp.jobLocation,
							missionDescription: valueOf("missionDescription"),
							results: valueOf("results"),
							technicalStack: valueOf("technicalStack"),
							skills: exp.skills.map((s: any) => ({
								id: s.id ?? 0,
								skillId: s.skill.id,
								freelancerProfileProfessionalExperienceId: exp.id
							})),
							businessDomains: exp.businessDomains.map((bd: any) => ({
								id: bd.id ?? 0,
								businessDomainId: bd.businessDomain.id,
								freelancerProfileProfessionalExperienceId: exp.id
							})),
							documents: exp.documents,
							urls: exp.urls
						} as any,
						languageCode.toLowerCase()
					);
					tf.markLanguageSaved(languageCode);
				}
			}

			// Scoped invalidations: the floating save touched the profile's presentation and/or
			// experience cells — not every translation query mounted on the page. Flags too: the
			// badge and AI toggle read them.
			// Staggered (async worker): profile cells scoped, experiences by type (the floating
			// save may have touched several of them).
			scheduleTranslationRefresh(queryClient, "FreelancerProfile", freelancerProfile!.id);
			scheduleTranslationRefresh(queryClient, "FreelancerProfileProfessionalExperience");
		};
		setSaveTranslationsAsync(saveTranslations);
		return () => setSaveTranslationsAsync(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [freelancerProfile, setSaveTranslationsAsync, queryClient]);

	// Returns the presentation value with EN fallback if FR is empty
	const getPresentationWithFallback = () => {
		const frVal = formik.values.presentation;
		if (frVal && frVal !== "<p></p>") return frVal;
		// Fallback to the EN value explicitly (not getFieldValue, which reads the language the
		// user happens to be DISPLAYING — validating from the FR tab would miss the EN content).
		const enVal = translatableFormRef.current.getValuesForLanguage(LanguageCode.En).presentation;
		return (enVal && enVal !== "<p></p>") ? enVal : frVal;
	};

	// Validation helper: considers EN fallback when FR presentation is empty
	const getValuesForValidation = () => ({
		...formik.values,
		presentation: getPresentationWithFallback()
	});

	// Track EN presentation value for re-triggering validation
	const enPresentationValue = translatableForm.getFieldValue("presentation");

	useEffect(() => {
		setFreelancerOnboardingMyPresentationStepViewModel({
			...formik.values,
			form: {
				isValid: formValidation.isValidSync(getValuesForValidation()),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty || !!enPresentationValue
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values, enPresentationValue, isFilledInAnyLanguage]);

	useEffect(() => {
		setTimeout(() => {
			setFreelancerOnboardingMyPresentationStepViewModel({
				...formik.values,
				form: {
					isValid: formValidation.isValidSync(getValuesForValidation()),
					isSubmitting: formik.isSubmitting,
					isDirty: !isCityValueChangedOnLoadBecauseOfBeingTranslated
				}
			});
		}, 200);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCityValueChangedOnLoadBecauseOfBeingTranslated, i18n.language]);

	if (isFreelancerProfileLoading) {
		return <CircularProgress />;
	}

	return (
		<form onSubmit={formik.handleSubmit}>
			<Card
				component={Paper}
				elevation={0}
				sx={{
					margin: "32px"
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography
						variant="h2"
						component="h1"
						gutterBottom
						sx={{ fontWeight: "bold", color: zoullooColors.primary }}
					>
						{t("freelancerOnboardingMyPresentation.title")}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ marginBottom: 3 }}>
						{t("freelancerOnboardingMyPresentation.subtitle")}
					</Typography>{" "}
					{/* Presentation Section */}
					<FreelancerOnboardingMyPresentationPresentationBloc
						formik={formik}
						editingLanguage={translatableForm.editingLanguage}
						onLanguageChange={translatableForm.switchLanguage}
						presentationValue={translatableForm.getFieldValue("presentation")}
						isStale={translatableForm.isFieldStale("presentation")}
						uiState={translatableForm.getFieldUiState("presentation")}
						sourceLanguage={translatableForm.sourceLanguage}
						showLanguageSelector={translatableForm.isLanguageSelectorVisible("presentation")}
						showAiFirstNote={translatableForm.isAiFirstNoteVisible("presentation")}
						isDraft={translatableForm.isFieldDraft("presentation")}
						onBeginDraft={() => translatableForm.beginFieldDraft("presentation")}
						onCancelDraft={() => {
							const restored = translatableForm.cancelFieldDraft("presentation");
							// Formik mirrors the FR value live while the draft is typed: resync on cancel.
							if (translatableForm.editingLanguage === LanguageCode.Fr) {
								formik.setFieldValue("presentation", restored);
							}
						}}
						canHandBackToAi={translatableForm.isFieldHandBackable("presentation")}
						isRegenerating={translatableForm.isFieldRegenerating("presentation")}
						onEnableAi={() => translatableForm.enableAiForField("presentation")}
						contentReadyToken={translatableForm.getFieldContentToken("presentation")}
						readOnly={translatableForm.isFieldReadOnly("presentation")}
						onPresentationChange={(value) => {
							translatableForm.setFieldValue("presentation", value);
							// Only sync formik when editing in primary language (FR)
							// Formik must always hold the FR value for validation and save
							if (translatableForm.editingLanguage === LanguageCode.Fr) {
								formik.setFieldValue("presentation", value);
							}
						}}
					/>
					<Divider sx={{ marginY: 3 }} />
					{/* Experiences Section */}
					<FreelancerOnboardingMyPresentationExperiencesBloc
						formik={formik}
						freelancerProfile={freelancerProfile!}
						experienceEditMode={experienceEditMode}
						setExperienceEditMode={setExperienceEditMode}
						setIsCityValueChangedOnLoadBecauseOfBeingTranslated={
							setIsCityValueChangedOnLoadBecauseOfBeingTranslated
						}
						experienceTranslatableFormsRef={experienceTranslatableFormsRef}
					/>
					<Divider sx={{ marginY: 3 }} />
					{/* Trainer Section */}
					<FreelancerOnboardingMyPresentationTrainerBloc formik={formik} />
					{/* Navigation Buttons */}
					<Grid2 container spacing={2} columns={12} direction={"column"} mt={10}>
						<Grid2 size={{ sm: 12, md: 4 }}>
							<Button
								type="submit"
								color="secondary"
								variant="contained"
								disabled={
									!formValidation.isValidSync(getValuesForValidation()) ||
									formik.isSubmitting ||
									Object.values(experienceEditMode).some(isEditing => isEditing)
								}
								fullWidth
							>
								{freelancerProfile?.onboarding.currentStatus !==
								FreelancerOnboardingStatus.Accepted
									? t("freelancerOnboardingMyPresentation.buttons.myAvailabilities")
									: t("common.save")}
							</Button>
						</Grid2>
						{freelancerProfile?.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted && (
							<Grid2 size={{ sm: 12, md: 4 }}>
								<Button
									color="secondary"
									variant="outlined"
									onClick={async () => {
										interceptNavigation({
											onValidNavigation: async () => {
												try {
													// Translation cells FIRST: the step-progress endpoint checks that
													// the presentation exists in at least one language AT CALL TIME.
													// A first-visit presentation only exists in the translatable form,
													// so its cell must be persisted before the step call or it 400s
													// (translation-only patches skip the step validation).
													const modifiedLangs = translatableFormRef.current.getModifiedLanguages();
													for (const { languageCode, fields } of modifiedLangs) {
														// Send the edited value even when empty (clearing must persist).
														if (!("presentation" in fields)) continue;
														await saveFreelancerOnboardingProgression(
															freelancerProfile!,
															[{ op: JSONPatchOperationType.Replace, path: "/presentation", value: fields.presentation }],
															languageCode.toLowerCase()
														);
														translatableFormRef.current.markLanguageSaved(languageCode);
													}

													await saveMyPresentationFreelancerOnboardingProgress.mutateAsync(
														{
															freelancerProfile: freelancerProfile!,
															freelancerOnboardingMyPresentationStepViewModel:
																formik.values,
															newStep: FreelancerOnboardingFormStep.MySectors
														}
													);

													scheduleTranslationRefresh(queryClient, "FreelancerProfile", freelancerProfile!.id);

													setCurrentOnboardingFormStep(
														FreelancerOnboardingFormStep.MySectors
													);
												} catch (error) {
													console.error(
														"Error returning back to the previous step:",
														error
													);
												}
											},
											onBlockedNavigation: () => {
												setNextOnboardingStep(FreelancerOnboardingFormStep.MySectors);
											}
										});
									}}
									disabled={formik.isSubmitting}
									fullWidth
								>
									{t("freelancerOnboardingMyPresentation.buttons.back")}
								</Button>
							</Grid2>
						)}
					</Grid2>
				</CardContent>
			</Card>
		</form>
	);
}
